import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2, Pencil, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import EmotionChipPicker from '../EmotionChipPicker';
import {
  emotionToMoodCode,
  emotionToBg,
  emotionToFg,
} from '../../utils/emotions';
import { hasLabel, resolveLabel } from '../../utils/moodScale';
import {
  AudioStrip,
  PendingEmbedDot,
  ProcessingIndicator,
  SegmentedRawText,
  normalizeForCompare,
} from './primitives';

const SAGE = '#7C9A82';
const AMBER = '#D4A574';

const MOOD_EMOJI = {
  rough: '😞', low: '🙁', neutral: '😐', good: '🙂', great: '😄',
};

const timeLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

/**
 * One Before/After chip. Collapsed it shows the emotion pill, the legacy mood
 * emoji (pre-v2 rows), or — only while the card is expanded — a dashed
 * "add" affordance. Tapping expands the picker in place.
 *
 * Selecting a chip is a *pending* edit: this component never talks to the API,
 * the parent commits it. An amber dot marks the dirty state.
 */
function MoodChip({ label, emotion, mood, pending = false, onChange, busy, offerWhenEmpty }) {
  const [editing, setEditing] = useState(false);
  const wrapperRef = useRef(null);

  const labelText = resolveLabel(emotion, mood);
  const bg = emotionToBg(emotion);
  const fg = emotionToFg(emotion);
  // The legacy emoji only stands in when there is no v2 emotion to show.
  const emoji = bg ? null : MOOD_EMOJI[mood];
  const hasValue = Boolean(labelText);

  useEffect(() => {
    if (!editing) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setEditing(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setEditing(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [editing]);

  // Same-chip taps close the popover without firing onChange, so re-picking a
  // value you already had never marks the card dirty or re-queues an embed.
  const handleSelect = useCallback((next) => {
    setEditing(false);
    if (next === emotion) return;
    onChange(next);
  }, [onChange, emotion]);

  if (!hasValue && !offerWhenEmpty) return null;

  return (
    <div ref={wrapperRef} className="flex flex-col items-start gap-2 min-w-0">
      <button
        type="button"
        onClick={() => setEditing((v) => !v)}
        disabled={busy}
        aria-expanded={editing}
        aria-label={`${label}: ${labelText || 'not set'}${pending ? ' (unsaved)' : ''}`}
        className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors duration-300 ease-out disabled:opacity-50"
        style={{
          color: fg || SAGE,
          backgroundColor: bg || 'transparent',
          border: hasValue ? '1px solid transparent' : `1px dashed ${SAGE}66`,
        }}
      >
        {emoji && <span className="text-base leading-none" aria-hidden="true">{emoji}</span>}
        <span>{labelText || `Add ${label.toLowerCase()}`}</span>
        {pending && (
          <span
            aria-hidden="true"
            title="Unsaved change"
            className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-white dark:ring-stone-800"
            style={{ backgroundColor: AMBER }}
          />
        )}
      </button>
      {editing && (
        <div className="pt-1 w-full">
          <EmotionChipPicker value={emotion} onChange={handleSelect} size="sm" ariaLabel={label} />
        </div>
      )}
    </div>
  );
}

/**
 * The card. One renderer for every voice note, replacing what used to be three
 * (VoiceNoteEntry for journal takes, EchoCard for echoes, VoiceNoteCard on the
 * journal page).
 *
 * Collapsed it is deliberately quiet: the take, its feelings if any were named,
 * two lines of what was said, and when. Everything else — the full text, the
 * editor, the chips you have not filled in — waits behind a tap, because a list
 * of ten cards that each shout their controls is a list nobody scans.
 *
 * What it no longer carries, and why:
 *   • Cleaned/Raw tabs — cleanup is disabled in production, so "cleaned" was
 *     always either the user's edit or the raw text under a second name.
 *   • "Save to journal" as a routine step — transcription accepts on its own
 *     since 2026-09-23. Save now appears only when there is an actual unsaved
 *     edit to commit, which is the only time it ever meant anything.
 */
function VoiceCardImpl({
  note,
  onDeleted,
  onEdited,
  onAccepted,
  onMoodChanged,
  onOpenJournal,
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(null); // 'edit' | 'save' | 'delete' | null
  const [error, setError] = useState(null);

  // Committed affect (server truth) and the pending picks layered over it.
  const [emotionBefore, setEmotionBefore] = useState(note.emotion_before ?? null);
  const [emotionAfter, setEmotionAfter] = useState(note.emotion_after ?? null);
  const [moodBefore, setMoodBefore] = useState(note.mood_before ?? null);
  const [moodAfter, setMoodAfter] = useState(note.mood_after ?? null);
  const [pendingBefore, setPendingBefore] = useState(null);
  const [pendingAfter, setPendingAfter] = useState(null);

  const displayBefore = pendingBefore ?? emotionBefore;
  const displayAfter = pendingAfter ?? emotionAfter;

  const baseText = note.user_edited_text || note.accepted_text || note.raw_transcript || '';
  const isUneditedRaw = !note.user_edited_text && !note.accepted_text && !!note.raw_transcript;

  const hasPendingMood =
    (pendingBefore !== null && pendingBefore !== emotionBefore) ||
    (pendingAfter !== null && pendingAfter !== emotionAfter);

  const hasUnsavedText =
    !!note.user_edited_text &&
    normalizeForCompare(note.user_edited_text) !== normalizeForCompare(note.accepted_text);

  const showSave = hasPendingMood || (note.status === 'accepted' && hasUnsavedText);

  const anyLabel =
    hasLabel(displayBefore, moodBefore) || hasLabel(displayAfter, moodAfter);

  const commit = useCallback(async () => {
    setBusy('save');
    setError(null);
    try {
      if (hasPendingMood) {
        const body = {};
        if (pendingBefore !== null && pendingBefore !== emotionBefore) {
          body.emotion_before = pendingBefore;
          // null for 'not_sure' — it has no valence bucket, by design.
          body.mood_before = emotionToMoodCode(pendingBefore);
        }
        if (pendingAfter !== null && pendingAfter !== emotionAfter) {
          body.emotion_after = pendingAfter;
          body.mood_after = emotionToMoodCode(pendingAfter);
        }
        const { data } = await api.patch(`/api/voice/${note.id}/mood`, body);
        setEmotionBefore(data.emotion_before ?? null);
        setEmotionAfter(data.emotion_after ?? null);
        setMoodBefore(data.mood_before ?? null);
        setMoodAfter(data.mood_after ?? null);
        setPendingBefore(null);
        setPendingAfter(null);
        onMoodChanged?.(note.id, data);
      }
      if (hasUnsavedText) {
        await api.post(`/api/voice/${note.id}/accept`);
        await onAccepted?.(note.id);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not save');
    } finally {
      setBusy(null);
    }
  }, [
    note.id, hasPendingMood, hasUnsavedText, pendingBefore, pendingAfter,
    emotionBefore, emotionAfter, onMoodChanged, onAccepted,
  ]);

  const saveEdit = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setError("Can't save an empty note.");
      return;
    }
    setBusy('edit');
    setError(null);
    try {
      await api.post(`/api/voice/${note.id}/edit`, { text: trimmed });
      // Re-accept immediately: an edit the user has to save twice is an edit
      // that gets lost. The worker replaces this note's chunks on re-embed.
      await api.post(`/api/voice/${note.id}/accept`);
      setEditing(false);
      onEdited?.(note.id);
      await onAccepted?.(note.id);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not save');
    } finally {
      setBusy(null);
    }
  }, [draft, note.id, onEdited, onAccepted]);

  const handleDelete = useCallback(async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this recording? The audio will be permanently removed.')) return;
    setBusy('delete');
    try {
      await api.delete(`/api/voice/${note.id}`);
      onDeleted?.(note.id);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not delete');
      setBusy(null);
    }
  }, [note.id, onDeleted]);

  // --- body ------------------------------------------------------------
  let body;
  if (note.status === 'pending') {
    body = <ProcessingIndicator label="Writing it down…" note={note} />;
  } else if (note.status === 'audio_only') {
    body = (
      <p className="text-stone-500 dark:text-stone-400 leading-relaxed">
        Saved as audio. Turn on AI in your profile and we'll write it down for you.
      </p>
    );
  } else if (note.status === 'empty_speech') {
    body = (
      <p className="text-stone-500 dark:text-stone-400 leading-relaxed">
        We couldn't hear any speech in this one.
      </p>
    );
  } else if (note.status === 'failed') {
    body = (
      <div className="space-y-1.5">
        <p className="text-stone-700 dark:text-[#F0EDE8]">We couldn't write this one down.</p>
        {note.last_error ? <p className="text-xs text-stone-400">{note.last_error}</p> : null}
      </div>
    );
  } else if (editing) {
    body = (
      <div className="space-y-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={Math.min(14, Math.max(4, draft.split('\n').length + 2))}
          className="w-full rounded-2xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900/40 px-5 py-4 text-lg leading-relaxed text-stone-800 dark:text-[#F0EDE8] focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': SAGE }}
          aria-label="Edit transcript"
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={saveEdit}
            disabled={busy === 'edit'}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 ease-out active:scale-95 disabled:opacity-60"
            style={{ backgroundColor: SAGE }}
          >
            {busy === 'edit' && <Loader2 size={14} className="animate-spin" />}
            Save changes
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setError(null); }}
            className="text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  } else if (baseText) {
    // Untouched raw text keeps its confidence underlines; anything the user or
    // the pipeline has since rewritten is shown plainly, because the segment
    // offsets no longer describe it.
    body = expanded ? (
      isUneditedRaw ? (
        <SegmentedRawText
          rawText={note.raw_transcript}
          segments={note.segments}
          lowConfidenceSegments={note.low_confidence_segments}
        />
      ) : (
        <p className="text-lg leading-relaxed text-stone-800 dark:text-[#F0EDE8] whitespace-pre-wrap">
          {baseText}
        </p>
      )
    ) : (
      <p className="text-lg leading-relaxed text-stone-800 dark:text-[#F0EDE8] line-clamp-2">
        {baseText}
      </p>
    );
  }

  const canExpand = Boolean(baseText) && !editing;

  return (
    <article className="bg-white dark:bg-stone-800/40 rounded-2xl sm:rounded-3xl shadow-sm p-4 sm:p-6 space-y-3 sm:space-y-4 overflow-hidden transition-shadow duration-300 ease-out">
      <AudioStrip note={note} />

      {(anyLabel || expanded) && (
        <div className="flex flex-wrap items-center gap-2">
          <MoodChip
            label="Before"
            emotion={displayBefore}
            mood={moodBefore}
            pending={pendingBefore !== null && pendingBefore !== emotionBefore}
            onChange={setPendingBefore}
            busy={busy === 'save'}
            offerWhenEmpty={expanded}
          />
          {anyLabel && <span aria-hidden="true" className="text-stone-300 dark:text-stone-600">→</span>}
          <MoodChip
            label="After"
            emotion={displayAfter}
            mood={moodAfter}
            pending={pendingAfter !== null && pendingAfter !== emotionAfter}
            onChange={setPendingAfter}
            busy={busy === 'save'}
            offerWhenEmpty={expanded}
          />
        </div>
      )}

      {body}

      {error && <p className="text-xs text-rose-500">{error}</p>}

      <div className="flex items-center gap-3 pt-0.5 text-xs text-stone-400 dark:text-stone-500">
        <span className="tabular-nums">{timeLabel(note.created_at)}</span>
        <PendingEmbedDot embeddingStatus={note.embedding_status} />

        {onOpenJournal && note.entry_date && (
          <>
            <span aria-hidden="true">·</span>
            <button
              type="button"
              onClick={() => onOpenJournal(note.entry_date)}
              className="underline decoration-dotted underline-offset-2 hover:text-stone-600 dark:hover:text-stone-300 transition-colors duration-300"
            >
              in your journal
            </button>
          </>
        )}

        <div className="ml-auto flex items-center gap-1">
          {showSave && (
            <button
              type="button"
              onClick={commit}
              disabled={busy === 'save'}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold text-white transition-transform duration-300 ease-out active:scale-95 disabled:opacity-60"
              style={{ backgroundColor: SAGE }}
            >
              {busy === 'save' && <Loader2 size={12} className="animate-spin" />}
              Save
            </button>
          )}
          {baseText && !editing && (
            <button
              type="button"
              onClick={() => { setDraft(baseText); setEditing(true); setExpanded(true); }}
              aria-label="Edit transcript"
              title="Edit"
              className="p-1.5 rounded-full hover:bg-stone-100 dark:hover:bg-stone-700/60 transition-colors duration-300"
            >
              <Pencil size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy === 'delete'}
            aria-label="Delete recording"
            title="Delete"
            className="p-1.5 rounded-full hover:bg-stone-100 dark:hover:bg-stone-700/60 hover:text-rose-500 transition-colors duration-300 disabled:opacity-40"
          >
            <Trash2 size={14} />
          </button>
          {canExpand && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={expanded ? 'Show less' : 'Show more'}
              className="p-1.5 rounded-full hover:bg-stone-100 dark:hover:bg-stone-700/60 transition-colors duration-300"
            >
              <ChevronDown
                size={14}
                className="transition-transform duration-300 ease-out motion-reduce:transition-none"
                style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
              />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

// A list re-renders on every poll tick. Without this, one card's status change
// repaints all of them — and each card owns an audio element.
export const VoiceCard = memo(VoiceCardImpl, (a, b) => {
  const x = a.note;
  const y = b.note;
  return (
    x.id === y.id &&
    x.status === y.status &&
    x.embedding_status === y.embedding_status &&
    x.user_edited_text === y.user_edited_text &&
    x.accepted_text === y.accepted_text &&
    x.raw_transcript === y.raw_transcript &&
    x.emotion_before === y.emotion_before &&
    x.emotion_after === y.emotion_after &&
    x.duration_ms === y.duration_ms &&
    a.onOpenJournal === b.onOpenJournal
  );
});

export default VoiceCard;
